import {
  Link,
  useLocation,
  useMatches,
  useNavigate,
  useRouteLoaderData,
} from "@remix-run/react";
import { motion } from "framer-motion";
import { ChevronDown, UserRound } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MobileMenu } from "~/components/common/MobileMenu";
import MenuIcon from "~/components/icons/MenuIcon";
import { Button } from "~/components/ui/Button";
import { fallbackLng, supportedLngs } from "~/i18n";
import { cn, getMenuHref } from "~/lib/utils";
import { useAppStore } from "~/stores/app";
import { useShopCartStore } from "~/stores/shopCart";
import LogoIcon from "../icons/LogoIcon";
import LogoSimple from "../icons/LogoSimple";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/DropdownMenu";
import { BookingCartIcon } from "./BookingCartIcon";
import { ShopCartIcon } from "./ShopCartIcon";

interface NavBarProps {
  variant?: "default" | "transparent";
  navClassName?: string;
  isScrolled?: boolean;
  bookingDisabled?: boolean;
  shopDisabled?: boolean;
}

export const NavBar = ({
  variant = "default",
  navClassName,
  isScrolled = false,
  bookingDisabled = false,
  shopDisabled = false,
}: NavBarProps = {}) => {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { mobileMenuOpened, toggleMobileMenu, openAuthModal } = useAppStore();
  const rootData = useRouteLoaderData<any>("root");
  // Se siamo su una pagina prodotto shop, recuperiamo gli slug tradotti dal
  // loader di quella route (per lo switch lingua senza 404). Usiamo useMatches
  // + endsWith per non dipendere dall'esatto id della route.
  const matches = useMatches();
  const productLocalizedSlugs = (
    matches.find((m) => m.id.endsWith("shop.$productSlug"))?.data as any
  )?.localizedSlugs as Record<string, string> | undefined;
  const { shopCart } = useShopCartStore();
  const token = rootData?.token;
  const voucherDisabled = rootData?.voucherDisabled;

  const isBookingPage = location.pathname.includes("/booking");

  const [languageSwitchOpened, setLanguageSwitchOpened] = useState(false);
  const [bookingMenuOpened, setBookingMenuOpened] = useState(false);

  const handleUserIconClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!token) {
      openAuthModal("login");
    } else {
      navigate(getMenuHref(i18n.language, "/user/account"));
    }
  };

  const handleChangeLanguage = async (newLang: string) => {
    i18n.changeLanguage(newLang);

    // Pagina prodotto shop: lo slug del prodotto è diverso per lingua, quindi
    // cambiare solo il prefisso locale darebbe 404. Usiamo lo slug tradotto; se
    // la traduzione non esiste, andiamo allo shop invece di mostrare una 404.
    if (productLocalizedSlugs) {
      const translatedSlug = productLocalizedSlugs[newLang];
      navigate(
        translatedSlug
          ? getMenuHref(newLang, `/shop/${translatedSlug}`)
          : getMenuHref(newLang, "/shop")
      );
      return;
    }

    const cleanedPathname = location.pathname.replace(
      new RegExp(`^/(${supportedLngs.join("|")})(/|$)`),
      "/"
    );
    if (newLang === fallbackLng) {
      navigate(cleanedPathname);
      return;
    }
    navigate(`${newLang}${cleanedPathname}`);
  };

  const isTransparent = variant === "transparent";

  return (
    <nav
      className={cn(
        "container mx-auto flex items-center justify-between py-7 lg:py-8 px-4 lg:px-4 relative",
        navClassName
      )}
    >
      <div className="flex items-center z-[2] relative">
        <Button variant="ghost" size="icon" aria-label="Apri menu">
          <MenuIcon
            isOpened={mobileMenuOpened}
            onClick={toggleMobileMenu}
            className={cn(
              "relative z-50 transition-colors duration-300",
              isTransparent && !isScrolled && "text-white",
              mobileMenuOpened && "text-white"
            )}
          />
        </Button>
        <MobileMenu bookingDisabled={bookingDisabled} />
        {!rootData.i18nDisabled && (
          <div className="ml-4 z-[30]">
            <DropdownMenu onOpenChange={setLanguageSwitchOpened} modal={false}>
              <DropdownMenuTrigger className="shadow-none !border-none appearance-none outline-none min-w-10">
                <div className="flex items-center select-none">
                  <span
                    className={cn("uppercase transition-colors duration-300", {
                      "text-white":
                        mobileMenuOpened || (isTransparent && !isScrolled),
                    })}
                  >
                    {i18n.language}
                  </span>
                  <ChevronDown
                    size={16}
                    className={cn(
                      "pt-0.5transform transition-all duration-300",
                      {
                        "text-white":
                          mobileMenuOpened || (isTransparent && !isScrolled),
                      },
                      {
                        "rotate-180": languageSwitchOpened,
                      }
                    )}
                  />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className={cn("-ml-4", {
                  "bg-white": isScrolled && !mobileMenuOpened,
                })}
              >
                {supportedLngs.map((lang, index) => {
                  if (lang === i18n.language) return null;
                  return (
                    <DropdownMenuItem
                      className="uppercase cursor-pointer w-full flex px-4"
                      key={index}
                      onClick={() => handleChangeLanguage(lang)}
                    >
                      <div
                        className={cn(
                          "pr-1 py-1 hover:scale-110 transition-all duration-300"
                        )}
                      >
                        {lang}
                      </div>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      <div className="flex items-center justify-center absolute left-0 w-full top-0 py-6 z-0">
        <Link
          to={getMenuHref(i18n.language, "/")}
          className={cn(
            "text-xl font-bold text-gray-800 hover:text-gray-600 transition-colors duration-300",
            isTransparent && !isScrolled && "text-white"
          )}
        >
          <LogoIcon
            className={cn(
              "hidden lg:block w-24 lg:w-40 h-12 fill-secondary transition-all duration-300",
              isTransparent && !isScrolled && "fill-white",
              {
                "opacity-0": mobileMenuOpened,
                "opacity-100": !mobileMenuOpened,
              }
            )}
          />
          <LogoSimple
            className={cn(
              "lg:hidden w-16 fill-secondary transition-all duration-300",
              isTransparent && !isScrolled && "fill-white",
              {
                "opacity-0": mobileMenuOpened,
                "opacity-100": !mobileMenuOpened,
              }
            )}
          />
        </Link>
        {/* Two variants of the badge: the "positive" one has gold lettering
            that reads on a white bar; the light one is for the dark hero
            gradient (home before scrolling). Both are rendered so the swap
            on scroll never waits for a network fetch. */}
        {[
          { src: "/images/winner-icon.png", onDark: true },
          { src: "/images/winner-icon-positive.png", onDark: false },
        ].map(({ src, onDark }) => (
          <img
            key={src}
            src={src}
            alt="World Spa Awards 2026 Winner - Italy's Best Day Spa"
            className={cn(
              "ml-3 lg:ml-4 h-9 lg:h-12 w-auto transition-opacity duration-300",
              onDark === (isTransparent && !isScrolled) ? "block" : "hidden",
              {
                "opacity-0": mobileMenuOpened,
                "opacity-100": !mobileMenuOpened,
              }
            )}
          />
        ))}
      </div>

      {/* <ul className="hidden lg:flex items-center justify-start space-x-8 pl-12 flex-1">
        <li>
          <Link
            to={getMenuHref(i18n.language, "/")}
            className={cn("text-secondary", isTransparent && "text-white")}
          >
            <HomeIcon size={20} />
          </Link>
        </li>
        {navItems.map((item) => {
          const isActive = location.pathname === item.href;

          return (
            <li key={item.name} className="flex flex-col relative">
              <Link
                to={item.href}
                className={cn(
                  "text-primary font-medium",
                  isTransparent && "text-white"
                )}
              >
                {item.name}
              </Link>
              <div className="absolute -bottom-2 left-0 flex justify-center w-full">
                {isActive && <MenuIndicator className="w-20 min-w-20" />}
              </div>
            </li>
          );
        })}
      </ul> */}

      <div className="flex items-center space-x-4 z-[1] relative">
        <div className="flex items-center space-x-2">
          <div className="hidden lg:flex items-center space-x-4">
            <Link
              to={
                bookingDisabled
                  ? getMenuHref(i18n.language, "/coming-soon")
                  : getMenuHref(i18n.language, "/booking")
              }
              className={cn(
                "transition-colors duration-300",
                isTransparent && !isScrolled && "text-white",
                {
                  "opacity-0": mobileMenuOpened,
                  "opacity-100": !mobileMenuOpened,
                },
                {
                  "text-secondary":
                    isScrolled || !isTransparent || mobileMenuOpened,
                }
              )}
            >
              <span>{t("Prenota")}</span>
            </Link>
            <Link
              to={
                shopDisabled
                  ? getMenuHref(i18n.language, "/coming-soon")
                  : getMenuHref(i18n.language, "/shop")
              }
              className={cn(
                "transition-colors duration-300",
                isTransparent && !isScrolled && "text-white",
                {
                  "opacity-0": mobileMenuOpened,
                  "opacity-100": !mobileMenuOpened,
                },
                {
                  "text-secondary":
                    isScrolled || !isTransparent || mobileMenuOpened,
                }
              )}
            >
              <span>{t("Regala")}</span>
            </Link>
            {!bookingDisabled && !voucherDisabled && (
              <Link
                to={getMenuHref(i18n.language, "/redeem-voucher")}
                className={cn(
                  "transition-colors duration-300",
                  isTransparent && !isScrolled && "text-white",
                  {
                    "opacity-0": mobileMenuOpened,
                    "opacity-100": !mobileMenuOpened,
                  },
                  {
                    "text-secondary":
                      isScrolled || !isTransparent || mobileMenuOpened,
                  }
                )}
              >
                <span>{t("Ho un Voucher")}</span>
              </Link>
            )}
          </div>

          {!isBookingPage && shopCart.item_count > 0 && (
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{
                type: "spring",
                stiffness: 500,
                damping: 30,
              }}
              className={cn("ml-2 flex items-center", {
                "opacity-0": mobileMenuOpened,
                "opacity-100": !mobileMenuOpened,
              })}
            >
              <ShopCartIcon
                className={cn(
                  "transition-all duration-400 hover:scale-125",
                  isTransparent &&
                  !isScrolled &&
                  !mobileMenuOpened &&
                  "text-white",
                  {
                    "text-secondary":
                      isScrolled || !isTransparent || mobileMenuOpened,
                  }
                )}
              />
            </motion.div>
          )}

          {isBookingPage && (
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{
                type: "spring",
                stiffness: 500,
                damping: 30,
              }}
              className={cn("ml-2 flex items-center lg:hidden", {
                "opacity-0": mobileMenuOpened,
                "opacity-100": !mobileMenuOpened,
              })}
            >
              <BookingCartIcon
                className={cn(
                  "transition-all duration-400 hover:scale-125",
                  isTransparent &&
                  !isScrolled &&
                  !mobileMenuOpened &&
                  "text-white",
                  {
                    "text-secondary":
                      isScrolled || !isTransparent || mobileMenuOpened,
                  }
                )}
              />
            </motion.div>
          )}

          <button
            onClick={handleUserIconClick}
            className="ml-2 p-2 cursor-pointer hover:scale-125 transition-all duration-400"
          >
            <UserRound
              size={20}
              className={cn(
                "text-secondary transition-colors duration-300",
                isTransparent &&
                !isScrolled &&
                !mobileMenuOpened &&
                "text-white",
                {
                  "opacity-0": mobileMenuOpened,
                  "opacity-100": !mobileMenuOpened,
                }
              )}
            />
          </button>
        </div>
      </div>
      {(() => {
        const pathname = location.pathname;
        const shouldHideBottomBar =
          pathname.includes("/shop") ||
          pathname.includes("/cart") ||
          pathname.includes("/checkout") ||
          pathname.includes("/booking");

        if (shouldHideBottomBar) {
          return null;
        }

        return (
          <>
            <div className="flex lg:hidden items-center fixed bottom-0 left-0 w-full bg-white z-20">
              <div className="flex items-center w-full">
                <Link
                  to={getMenuHref(i18n.language, "/shop")}
                  className={cn(
                    "flex-1 border-[1px] border-secondary text-secondary p-4 py-4 text-center uppercase"
                  )}
                >
                  <span>{t("Regala")}</span>
                </Link>
              </div>

              <button
                onClick={() => setBookingMenuOpened(true)}
                className={cn(
                  "w-full bg-secondary border-[1px] border-secondary text-secondary-foreground p-4 py-4 text-center uppercase"
                )}
              >
                <span>{t("Prenota")}</span>
              </button>
            </div>

            {bookingMenuOpened && (
              <div
                className={cn(
                  "fixed inset-0 z-50 lg:hidden transition-all duration-300 w-full h-full",
                  {
                    "pointer-events-none": !bookingMenuOpened,
                    "pointer-events-all": bookingMenuOpened,
                  }
                )}
              >
                <div
                  className={cn(
                    "absolute inset-0 animate-in fade-in",
                  )}
                  onClick={() => setBookingMenuOpened(false)}
                >
                  <div className="absolute inset-0 bg-black opacity-50" />
                </div>
                <motion.div
                  initial={{ y: "100%" }}
                  animate={{ y: bookingMenuOpened ? 0 : "100%" }}
                  exit={{ y: "100%" }}
                  transition={{ type: "spring", damping: 30, stiffness: 300 }}
                  className="absolute bottom-0 left-0 w-full bg-white rounded-t-2xl shadow-xl"
                >
                  <div className="flex flex-col px-4">
                    <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mt-3 mb-4" />
                    <div className="flex flex-col py-8">
                      <Link
                        to={getMenuHref(i18n.language, "/booking")}
                        onClick={() => setBookingMenuOpened(false)}
                        className={cn(
                          "w-full bg-secondary border-[1px] border-secondary text-secondary-foreground p-4 py-4 text-center uppercase mb-3 rounded"
                        )}
                      >
                        <span>{t("Prenota")}</span>
                      </Link>
                      {!bookingDisabled && !voucherDisabled && (
                        <Link
                          to={getMenuHref(i18n.language, "/redeem-voucher")}
                          onClick={() => setBookingMenuOpened(false)}
                          className={cn(
                            "w-full border-[1px] border-secondary text-secondary p-4 py-4 text-center uppercase mb-4 rounded"
                          )}
                        >
                          <span>{t("Ho un Voucher")}</span>
                        </Link>
                      )}
                    </div>
                  </div>
                </motion.div>
              </div>
            )}
          </>
        );
      })()}
    </nav>
  );
};
